import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, exportToExcel,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge, ImportWizard } from './ui';
import {
  useC360List, C360Table, ColumnChooser,
  ViewIcon, EditIcon, DeleteIcon, UploadIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

// Kayıt görüntüleme/düzenleme tam-sayfa detaya (/rulecategories/:id) gider — bu ekran liste-only.
const API_BASE = '/api/RuleCategories';

const STATUS_OPTIONS = [{ value: 'true', label: 'Aktif' }, { value: 'false', label: 'Pasif' }];

const COLUMN_FILTER_META = {
  name: { type: 'text', odataField: 'Name' },
  description: { type: 'text', odataField: 'Description' },
  isActive: { type: 'select', odataField: 'IsActive', valueFormat: 'raw', options: STATUS_OPTIONS },
};
const SEARCH_FIELDS = ['Name', 'Description'];

export function RuleCategoryPage() {
  usePageMeta('Kural Kategorileri', 'Kategori kayıtlarını filtreleyin, sıralayın ve yönetin.');
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [importOpen, setImportOpen] = useState(false);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const list = useC360List({
    apiBase: API_BASE,
    columnFilterMeta: COLUMN_FILTER_META,
    searchFields: SEARCH_FIELDS,
    defaultSortField: 'Name',
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData, exportRows,
  } = list;

  const openNew = () => navigate('/rulecategories/new');
  const openDetail = (row = selected) => { if (!row) return; navigate(`/rulecategories/${row.id}`, { state: { row } }); };
  const openEditRow = (row = selected) => { if (!row) return; navigate(`/rulecategories/${row.id}?edit=1`, { state: { row } }); };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API_BASE}/${selected.id}`, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) throw new Error('Kayıt silinemedi');
      showToast('success', 'Kural kategorisi silindi');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Silme işlemi sırasında hata oluştu');
      setDeleteConfirm(false);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('name', {
      header: 'Kategori Adı', meta: { filterType: 'text' },
      cell: (info) => { const v = info.getValue(); return v ? <strong>{v}</strong> : '-'; },
    }),
    columnHelper.accessor('description', { header: 'Açıklama', meta: { filterType: 'text' }, cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('isActive', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => (info.getValue() ? <StatusBadge tone="ok">Aktif</StatusBadge> : <StatusBadge tone="neutral">Pasif</StatusBadge>),
    }),
    columnHelper.accessor('createdDate', {
      header: 'Oluşturma', enableColumnFilter: false,
      cell: (info) => { const v = info.getValue(); return v ? new Date(v).toLocaleDateString('tr-TR') : '-'; },
    }),
    columnHelper.accessor('updatedDate', {
      header: 'Güncelleme', enableColumnFilter: false,
      cell: (info) => { const v = info.getValue(); return v ? new Date(v).toLocaleDateString('tr-TR') : '-'; },
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Detay" aria-label="Detay" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Düzenle" aria-label="Düzenle" onClick={(e) => { e.stopPropagation(); openEditRow(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(true); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canEdit, canDelete]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('Name', 'asc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { field: 'name', headerName: 'Kategori Adı' },
        { field: 'description', headerName: 'Açıklama' },
        { headerName: 'Durum', valueGetter: ({ row }) => (row.isActive ? 'Aktif' : 'Pasif') },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      const count = await exportRows(cols, `kural-kategorileri_${dateStr}`);
      if (count === 0) showToast('error', 'Dışa aktarılacak veri yok');
      else showToast('success', `${count} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  // === İçe aktarma ===
  const importPick = (row, keys) => { for (const k of keys) { if (row[k] != null && row[k] !== '') return String(row[k]).trim(); } return ''; };
  const parseBool = (v) => !['hayır', 'hayir', 'pasif', 'false', '0', 'no'].includes(String(v ?? '').trim().toLowerCase());
  const importRowToPayload = (row) => ({
    name: importPick(row, ['Kategori Adı', 'Kategori Adi', 'Ad', 'Adı', 'name', 'Name']),
    description: importPick(row, ['Açıklama', 'Aciklama', 'description', 'Description']),
    isActive: parseBool(importPick(row, ['Durum', 'isActive', 'IsActive', 'Aktif'])),
  });
  const importValidateRow = (p) => (p.name ? null : 'Kategori adı boş');
  const importSubmitRow = async (p) => {
    try {
      const res = await fetch(API_BASE, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ name: p.name, description: p.description, isActive: p.isActive }) });
      if (res.ok) return { ok: true };
      return { ok: false, error: `Sunucu ${res.status}` };
    } catch (e) {
      return { ok: false, error: e.message || 'Network hatası' };
    }
  };
  const downloadImportTemplate = () => { exportToExcel([{ 'Kategori Adı': 'Örnek Kategori', 'Açıklama': 'Açıklama', 'Durum': 'Aktif' }], null, 'kural-kategorisi_sablon'); };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Kategori adı veya açıklama ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
        {canAdd && <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel/CSV'den İçe Aktar" aria-label="Excel/CSV'den İçe Aktar" onClick={() => setImportOpen(true)}><UploadIcon size={16} /></button>}
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Kural Kategorileri"
        subtitle="Kategori kayıtlarını filtreleyin, sıralayın ve yönetin."
        actions={canAdd ? <button style={newRecordButtonStyle} onClick={openNew}>+ Ekle</button> : null}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openDetail}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}><p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}><strong>"{selected?.name}"</strong> kuralı kategorisini silmek istediğinize emin misiniz?</p></div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <ImportWizard
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onComplete={(okCount) => { if (okCount > 0) { showToast('success', `${okCount} kural kategorisi içe aktarıldı`); fetchData(); } }}
        title="Kural Kategorisi İçe Aktar"
        helpText="Zorunlu alan: Kategori Adı. Açıklama opsiyonel. Durum: Aktif/Pasif (boş ise Aktif)."
        rowToPayload={importRowToPayload}
        validateRow={importValidateRow}
        submitRow={importSubmitRow}
        downloadTemplate={downloadImportTemplate}
      />
      <Toast toast={toast} />
    </div>
  );
}
